import {
  ExceptionErrorCode,
  FinancingConfigDto,
  getFirstOriginationFromStorage,
  getMerchantFinancingConfig,
  isEquivalentToSavedOrigination,
  reportErrorToServer,
  useMerchantById,
} from '@applyonline/shared'
import { AcceptTermsCheckbox, AsyncActionButton, ScrollableAlert } from '@applyonline/shared/components'
import { EFinancingProgram } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { ValidationError } from '@if/core/yup'
import { AxiosError } from 'axios'
import { useCallback, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { SubmitPrequalificationDto, useSubmitPrequalification } from '../../../api/credit-api'
import PromoCodeInput from '../../../components/PromoCodeInput'
import { useAppStore } from '../../../data/AppContext'
import { isDefaultMerchant } from '../../../data/merchant-selectors'
import { getRecaptchaToken } from '../../../services/recaptcha'
import { Prequalification, buildPrequalificationSchemaObject } from '../../../types/Schemas/PrequalificationSchema'
import ReviewApplicantInfo from './ReviewApplicantInfo'

interface Props {
  gotoStep: (step: string) => void
  prequalification: Prequalification
  financingProgramConfig: FinancingConfigDto
}

const ReviewPrequalification = ({ prequalification, gotoStep, financingProgramConfig }: Props) => {
  const { t } = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const navigate = useNavigate()

  const [hasDuplicateLoanRequest, setHasDuplicateLoanRequest] = useState(false)
  const [promoCodeIsValidating, setPromoCodeIsValidating] = useState<boolean>()
  const [merchantPaymentPlanId, setMerchantPaymentPlanId] = useState<string | null>(null)
  const [prequalPromoCode, setPrequalPromoCode] = useState<string | null>(null)
  const [submitPrequalification, isSubmitting, isSuccess] = useSubmitPrequalification()
  const [merchant] = useMerchantById(prequalification.merchantId, EFinancingProgram.Personal)
  const [hasLoanAmountError, setHasLoanAmountError] = useState<boolean>(false)
  const [hasApplicantError, setHasApplicantError] = useState<boolean>(false)
  const [hasApplicantAddressError, setHasApplicantAddressError] = useState<boolean>(false)
  const [hasApplicantRevenueError, setHasApplicantRevenueError] = useState<boolean>(false)
  const [hasFormError, setHasFormError] = useState<boolean>(false)
  const [isHandlerRunning, setIsHandlerRunning] = useState(false)
  const shouldDisplayAnyPromoCodeRelatedInfo = merchant?.supportsPromotions === true
  const appStore = useAppStore()
  const { applicant } = prequalification

  const handleOnPaymentPlanIdChange = useCallback((paymentPlanId: string | null, promoCodeInput: string | null) => {
    setMerchantPaymentPlanId(paymentPlanId)
    setPrequalPromoCode(promoCodeInput)
  }, [])

  const prequalificationDataContainsErrors = async () => {
    setHasLoanAmountError(false)
    setHasApplicantError(false)
    setHasApplicantAddressError(false)
    setHasApplicantRevenueError(false)
    setHasFormError(false)
    let formHasError = false
    try {
      await buildPrequalificationSchemaObject(financingProgramConfig).validate(prequalification, {
        abortEarly: false,
      })
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach((err) => {
          const path = err.path!
          if (path.includes('requestedLoanAmount')) {
            setHasLoanAmountError(true)
            formHasError = true
          }
          if (path.includes('applicant')) {
            setHasApplicantError(true)
            formHasError = true
          }
          if (path.includes('applicant.currentAddress')) {
            setHasApplicantAddressError(true)
            formHasError = true
          }
          if (path.includes('applicant.currentJobs')) {
            setHasApplicantRevenueError(true)
            formHasError = true
          }
        })
      }
    }
    if (formHasError) {
      setHasFormError(formHasError)
      window.scrollTo(0, 0)
    }
    return formHasError
  }

  const adjustAmountForMerchantConfig = async (merchantId: string, currentAmount: number): Promise<number> => {
    if (isDefaultMerchant(merchantId)) {
      return currentAmount
    }

    const config = await getMerchantFinancingConfig(merchantId, EFinancingProgram.Personal)

    if (currentAmount > config.maximumLoanAmount) {
      return config.maximumLoanAmount
    }
    if (currentAmount < config.minimumLoanAmount) {
      return config.minimumLoanAmount
    }
    return currentAmount
  }

  const buildDtoAndSubmitPrequal = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault()
    if (isHandlerRunning) return
    setIsHandlerRunning(true)
    try {
      let firstOrigination = getFirstOriginationFromStorage()
      let originationFromUrl = appStore.originationFromUrl

      if (originationFromUrl == null || isEquivalentToSavedOrigination(originationFromUrl)) {
        originationFromUrl = firstOrigination
        firstOrigination = null
      }

      if ((await prequalificationDataContainsErrors()) === false) {
        try {
          const adjustedAmount = await adjustAmountForMerchantConfig(
            prequalification.merchantId,
            prequalification.requestedLoanAmount,
          )
          const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_postCreditApplicationAndPrequalify')
          const dto: SubmitPrequalificationDto = {
            recaptchaToken,
            prequalification: {
              ...prequalification,
              requestedLoanAmount: adjustedAmount,
              lastTouchOrigination: originationFromUrl,
              firstTouchOrigination: firstOrigination,
              merchantPaymentPlanId,
              prequalPromoCode,
            },
          }
          const creditApp = await submitPrequalification(dto)
          navigate(`/creditApplication/${creditApp.id}`, { replace: true })
        } catch (error) {
          const axiosError = error as AxiosError<{ errorCode: string }>
          const errorCode = axiosError.response?.data?.errorCode
          if (axiosError.response?.status === 422 && errorCode === ExceptionErrorCode.RecaptchaException) {
            navigate('/recaptcha-validation-failed')
          } else if (errorCode === ExceptionErrorCode.DuplicateException) {
            setHasDuplicateLoanRequest(true)
          } else {
            reportErrorToServer(error as Error)
          }
        }
      }
    } finally {
      setIsHandlerRunning(false)
    }
  }

  return (
    <section className="step-content">
      <h3 className="form-question">{t('review.title')}</h3>
      <div className="paragraph">
        <p>{t('review.description')}</p>
      </div>

      <form onSubmit={buildDtoAndSubmitPrequal}>
        <ScrollableAlert showAlert={hasFormError} type="error" message={t('review.formContainsErrors')} />
        <ScrollableAlert
          showAlert={hasDuplicateLoanRequest}
          type="warn"
          message={t('contact.hasActiveLoanRequest')}
          link={t('contact.icebergPhone')}
          linkText={t('contact.icebergPhone')}
          linkType="tel"
        />
        <div className="review-wrap">
          <div className="review-item">
            <h1 className="h3">{t('review.desiredLoanAmount')}</h1>
            <div className="box">
              <div className="box-content">
                <p>
                  <strong>{t('loanAmount.amount')} : </strong>
                  <span>{formatCurrency(prequalification.requestedLoanAmount)}</span>
                </p>
              </div>
              <button
                type="button"
                className={hasLoanAmountError ? 'btn-error btn-simple-error' : 'btn-action btn-simple'}
                onClick={() => gotoStep('loan-amount')}
              >
                {t('review.modify')}
              </button>
            </div>
          </div>

          <ReviewApplicantInfo
            applicant={applicant}
            hasApplicantError={hasApplicantError}
            hasApplicantAddressError={hasApplicantAddressError}
            hasApplicantRevenueError={hasApplicantRevenueError}
            gotoStep={gotoStep}
          />

          {shouldDisplayAnyPromoCodeRelatedInfo && (
            <PromoCodeInput
              merchantId={merchant.id}
              onChange={handleOnPaymentPlanIdChange}
              setValidating={setPromoCodeIsValidating}
            />
          )}
        </div>
        <footer className="review-footer">
          <h3>
            <Trans i18nKey="confirmation.consentTitle" />
          </h3>

          <AcceptTermsCheckbox
            msgOnInvalid={t('common.acceptConditions')}
            id="acceptConditions"
            termId={t('confirmation.consent')}
          />

          <AcceptTermsCheckbox
            msgOnInvalid={t('common.acceptConditions')}
            id="acceptAuthorise"
            termId={t('common.acceptAuthorization')}
          >
            <br />
            {t('confirmation.authorization')}
            <u>
              <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                {t('contact.linkText')}
              </a>
            </u>
            {t('confirmation.authorizationEnd')}
          </AcceptTermsCheckbox>
          <AsyncActionButton
            type="submit"
            className="btn btn-blue"
            disabled={isHandlerRunning || isSubmitting || promoCodeIsValidating || hasFormError}
            isPending={isHandlerRunning || isSubmitting || isSuccess}
            primary
          >
            <span>{t('review.getQuote')}</span>
            <i className="fa-regular fa-arrow-right" />
          </AsyncActionButton>
        </footer>
      </form>
    </section>
  )
}

export default ReviewPrequalification
